import React, { useState, useRef, useEffect } from 'react';

const BackgroundMusic = () => {
  const audioRef = useRef(null);
  const hasTriedPlaying = useRef(false); // ref, not state — no re-renders
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);

  useEffect(() => {
    const removeListeners = () => {
      window.removeEventListener('scroll', tryPlay);
      window.removeEventListener('touchstart', tryPlay);
      window.removeEventListener('touchend', tryPlay);
      window.removeEventListener('click', tryPlay);
    };

    const tryPlay = () => {
      if (!audioRef.current || hasTriedPlaying.current) return;
      // Block concurrent calls — reset in catch so next gesture can retry
      hasTriedPlaying.current = true;

      audioRef.current
        .play()
        .then(() => {
          removeListeners();
          setIsPlaying(true);
        })
        .catch(() => {
          // Gesture was blocked (e.g. scroll on Android) — reset so next gesture tries again
          hasTriedPlaying.current = false;
        });
    };

    window.addEventListener('scroll', tryPlay);
    window.addEventListener('touchstart', tryPlay);
    window.addEventListener('touchend', tryPlay);
    window.addEventListener('click', tryPlay);

    const stopMusic = () => {
      if (!audioRef.current) return;
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      hasTriedPlaying.current = false;
      setIsPlaying(false);
    };

    const handleVisibilityChange = () => {
      if (document.hidden) stopMusic();
    };

    const handleWindowBlur = () => stopMusic();

    const handlePageHide = () => stopMusic();

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleWindowBlur);
    window.addEventListener('pagehide', handlePageHide);

    return () => {
      removeListeners();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleWindowBlur);
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, []);

  const handleButtonClick = () => {
    if (!audioRef.current) return;
    if (!isPlaying) {
      // Audio never started — attempt play on user gesture
      audioRef.current
        .play()
        .then(() => setIsPlaying(true))
        .catch(() => {
          // play() can fail if the element is not ready or gesture is insufficient; treat as no-op
        });
      return;
    }
    // Toggle mute
    const next = !isMuted;
    audioRef.current.muted = next;
    setIsMuted(next);
  };

  // Derive button appearance
  const isPulsing = !isPlaying;
  const isMutedAndPlaying = isPlaying && isMuted;
  const icon = isMutedAndPlaying ? '♪' : '♫';

  return (
    <>
      <audio
        ref={audioRef}
        src="/Leja.mp3"
        loop
        preload="auto"
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
      />
      <button
        onClick={handleButtonClick}
        aria-label={isMutedAndPlaying ? 'Unmute music' : isPlaying ? 'Mute music' : 'Play music'}
        className={[
          'fixed bottom-4 right-4 z-50',
          'w-10 h-10 rounded-full',
          'bg-olive/90 hover:bg-olive',
          'text-white text-lg',
          'flex items-center justify-center',
          'transition-all duration-200',
          'shadow-md',
          isPulsing ? 'animate-pulse' : '',
          isMutedAndPlaying ? 'opacity-60' : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {icon}
      </button>
    </>
  );
};

export default BackgroundMusic;